import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import RingChart from '../../../../charts/echarts/Ring'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => {
    return formatter(d, valueFormat)
  }, [formatter, valueFormat])

  return {
    valueFormatter
  }
}

function DDPMetricRing ({
  pipeline,
  variables,

  // Data structure configuration
  // For single value: don't specify dataAccessor, metrics will access root
  // For object/summary: specify path to the object
  dataAccessor, // Optional: path to data object (e.g., 'summary', 'metrics')

  // Metrics configuration
  metrics, // Array of { accessor, name, target }

  // Display configuration
  height = 300,
  width = 300,
  valueFormat = 'percentage',
  chartProps
}) {
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  // Transform data for ring chart
  const chartData = useMemo(() => {
    if (!data) return []

    // Get the data object to work with
    const sourceData = dataAccessor ? get(data, dataAccessor) : data

    // If sourceData is an array (like summary), convert to dictionary by key
    let dataDict = sourceData
    if (Array.isArray(sourceData)) {
      dataDict = sourceData.reduce((acc, item) => {
        if (item.key) {
          acc[item.key] = item
        }
        return acc
      }, {})
    }

    // Process each metric
    return metrics.map((metric) => {
      const value = get(dataDict, metric.accessor)

      // Calculate percentage value if target is provided
      let displayValue = Number(value)

      if (metric.target) {
        // Check if target is a string accessor or a hard-coded number
        const targetValue = typeof metric.target === 'string'
          ? get(dataDict, metric.target) // Accessor path
          : metric.target // Hard-coded number

        if (targetValue) {
          displayValue = Number(value) / Number(targetValue)
        }
      }

      return {
        value: displayValue,
        name: metric.name || '',
        title: {
          offsetCenter: metric.name ? ['0%', '-10px'] : ['0%', '0']
        },
        detail: {
          valueAnimation: true,
          fontSize: metric.name ? undefined : 32,
          offsetCenter: metric.name ? ['0%', '10px'] : ['0%', '0']
        }
      }
    })
  }, [data, dataAccessor, metrics])

  const { valueFormatter } = useFormatters(valueFormat)

  if (isLoading) return null

  return (
    <RingChart
      height={height}
      width={width}
      data={chartData}
      valueFormat={valueFormatter}
      chartOptions={chartProps}
    />
  )
}

DDPMetricRing.propTypes = {
  // Pipeline configuration
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,

  // Data structure configuration
  dataAccessor: PropTypes.string,

  // Metrics configuration
  metrics: PropTypes.arrayOf(PropTypes.shape({
    accessor: PropTypes.string.isRequired,
    name: PropTypes.string,
    target: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })).isRequired,

  // Display configuration
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueFormat: PropTypes.string,
  chartProps: PropTypes.object
}

DDPMetricRing.defaultProps = {
  height: 300,
  width: 300,
  valueFormat: 'percentage'
}

export default DDPMetricRing
